import { useState, useEffect, useCallback } from "react";
import strings from "../../../locales/ro.json";
import { useApi } from "../../../hooks/useApi";

export default function useRolesManager() {
  const [roleTypes, setRoleTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { api, authToken } = useApi();

  const clearError = () => setError(null);

  // ─── Fetch all data ────────────────────────────────────

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [roleTypesRes, rolesRes, usersRes] = await Promise.all([
        api("role-types"),
        api("roles"),
        api("users"),
      ]);
      if (Array.isArray(roleTypesRes)) setRoleTypes(roleTypesRes);
      if (Array.isArray(rolesRes)) setRoles(rolesRes);
      if (Array.isArray(usersRes)) setUsers(usersRes);
    } catch (err) {
      console.error("Error fetching roles data:", err);
      setError(strings.admin.roles.errors.fetch);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (authToken) fetchAll();
  }, [authToken, fetchAll]);

  // ─── Role CRUD ─────────────────────────────────────────

  const createRole = async ({ name, role_type_id, color }) => {
    setError(null);
    try {
      const newRole = await api("roles", "POST", { name, role_type_id, color });
      // Refetch to get the full role_type object joined
      await fetchAll();
      return newRole;
    } catch (err) {
      console.error("Error creating role:", err);
      setError(strings.admin.roles.errors.create);
      throw err;
    }
  };

  const updateRole = async (id, { name, color }) => {
    setError(null);
    try {
      await api(`roles/${id}`, "PATCH", { name, color });
      await fetchAll();
    } catch (err) {
      console.error("Error updating role:", err);
      setError(strings.admin.roles.errors.update);
      throw err;
    }
  };

  const deleteRole = async (id) => {
    setError(null);
    try {
      await api(`roles/${id}`, "DELETE");
      setRoles((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error("Error deleting role:", err);
      setError(strings.admin.roles.errors.delete);
      throw err;
    }
  };

  // ─── User-Role Assignment ──────────────────────────────

  const assignRole = async (userEmail, roleId) => {
    setError(null);
    const uid = userEmail.split("@")[0];
    try {
      await api(`users/${uid}/roles`, "POST", { role_id: roleId });
    } catch (err) {
      console.error("Error assigning role:", err);
      setError(strings.admin.roles.errors.assign);
      throw err;
    }
  };

  const removeRole = async (userEmail, roleId) => {
    setError(null);
    const uid = userEmail.split("@")[0];
    try {
      await api(`users/${uid}/roles/${roleId}`, "DELETE");
    } catch (err) {
      console.error("Error removing role:", err);
      setError(strings.admin.roles.errors.remove);
      throw err;
    }
  };

  return {
    roleTypes,
    roles,
    users,
    loading,
    error,
    clearError,
    fetchAll,
    createRole,
    updateRole,
    deleteRole,
    assignRole,
    removeRole,
  };
}
